<template>
  <el-col class="video-card-container">
    <div class="video-card-wrapper" :title="stripHtml(video.title)">
      <el-card :body-style="{ padding: '0px' }" class="video-card shadow-hover">
        <router-link target="_blank" :to="`/video/${video.videoId}`" class="cover-link">
          <div class="image-container">
            <el-image
              lazy
              fit="cover"
              class="cover-img"
              :src="video.coverUrl"
            />

            <div class="status-bar top-bar">
              <span class="left-tag">
                <i v-if="!video.cached" class="el-icon-warning-outline uncache-icon" />
              </span>
              <span class="duration-tag">{{ video.duration }}</span>
            </div>

            <div class="status-bar bottom-bar">
              <div class="data-group">
                <i v-if="video.horizontal" class="el-icon-monitor device-icon" />
                <i v-else class="el-icon-mobile-phone device-icon" />
                <span class="data-item"><i class="el-icon-video-play" /> {{ getVisited(video.view) }}</span>
                <span class="data-item"><i class="el-icon-chat-dot-round" /> {{ getVisited(video.comment) }}</span>
              </div>
            </div>
          </div>
        </router-link>

        <div class="content-body">
          <router-link class="title-link" target="_blank" :to="`/video/${video.videoId}`">
            <h3 class="video-title" v-html="video.title"></h3>
          </router-link>

          <div class="meta-info">
            <template v-if="video.user">
              <router-link class="user-link" target="_blank" :to="`/user/${video.user.userId}`">
                <i class="el-icon-user"></i> {{ video.user.screenName | ellipsisUsername }}
              </router-link>
              <span class="dot-split">•</span>
            </template>
            <span class="date-text">{{ video.pubDateStr }}</span>
          </div>
        </div>
      </el-card>
    </div>
  </el-col>
</template>

<script>
import { handleVisited } from 'assets/js/utils'

export default {
  name: 'SearchVideoCard',
  filters: {
    ellipsisUsername(value) {
      if (!value) return ''
      const max = 8 // 增加了一点用户名的可见长度
      return value.length > max ? value.slice(0, max) + '...' : value
    }
  },
  props: {
    video: {
      type: Object,
      default: null
    },
    dateTit: {
      type: String,
      default: ''
    }
  },
  methods: {
    // 过滤掉所有的 HTML 标签
    stripHtml(html) {
      if (!html) return '';
      return html.replace(/<[^>]+>/g, '');
    },
    getVisited(visited) {
      return handleVisited(visited)
    }
  }
}
</script>

<style lang="scss" scoped>
.video-card-container {
  padding: 8px; // 统一通过容器控制间距
}

.video-card-wrapper {
  cursor: pointer;
  width: 100%;
}

.video-card {
  border: none !important;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: #fff;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
  }
}

.image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9; // 锁定比例，防止布局跳动
  background: #f0f0f0;
  overflow: hidden;

  .cover-img {
    width: 100%;
    height: 100%;
    transition: transform 0.5s;
  }

  &:hover .cover-img {
    transform: scale(1.05);
  }
}

/* 状态栏通用样式 */
.status-bar {
  position: absolute;
  left: 0;
  right: 0;
  padding: 8px 10px;
  color: #fff;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
  pointer-events: none;
}

.top-bar {
  top: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);

  .uncache-icon {
    color: #ff4d4f;
    font-weight: bold;
    font-size: 14px;
    filter: drop-shadow(0 0 2px rgba(0,0,0,0.5));
  }
}

.bottom-bar {
  bottom: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.6), transparent);

  .data-group {
    display: flex;
    align-items: center;
    gap: 12px;

    .device-icon {
      font-size: 14px;
      margin-right: -4px;
    }

    .data-item {
      display: flex;
      align-items: center;
      gap: 3px;
      text-shadow: 0 1px 2px rgba(0,0,0,0.8);
    }
  }
}

/* 文字内容区域 */
.content-body {
  padding: 12px;

  .title-link {
    text-decoration: none;
    display: block;
    margin-bottom: 8px;
  }

  .video-title {
    margin: 0;
    font-size: 14px;
    color: #222;
    line-height: 1.5;
    height: 42px; // 固定两行高度
    font-weight: 500;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.2s;

    &:hover {
      color: #409EFF;
    }

    /* 处理搜索高亮词的颜色 */
    ::v-deep em {
      color: #f73131;
      font-style: normal;
    }
  }
}

.meta-info {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #9499a0;
  white-space: nowrap;
  overflow: hidden;

  .user-link {
    text-decoration: none;
    color: #61666d;
    display: flex;
    align-items: center;
    gap: 4px;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: #409EFF;
    }
  }

  .dot-split {
    margin: 0 6px;
  }

  .date-text {
    flex-shrink: 0;
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .video-card-container {
    padding: 5px; // 手机端缩小边距
  }

  .content-body {
    padding: 8px;

    .video-title {
      font-size: 13px;
      height: 38px;
      line-height: 1.4;
    }
  }

  .status-bar {
    padding: 4px 6px;
    font-size: 10px;
  }

  .meta-info {
    font-size: 11px;
  }
}
</style>
